/**
 * First-run onboarding, and the home screen instruction.
 *
 * This screen exists mostly for one paragraph. On iOS, Safari delivers Web Push
 * only to a PWA that has been added to the home screen -- in a normal Safari
 * tab the subscribe call is not even available. A captain who skips this step
 * gets an app that works perfectly and silently never notifies them about an
 * approval, which is the single failure this product cannot afford.
 *
 * So the instruction is shown before the notification prompt, not after, and it
 * is shown as a step to complete rather than a note to read.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Notice } from '@abg/ui'
import { useAuth } from '../lib/auth'
import { isIos, isStandalone, pushSupport, subscribe, type PushSupport } from '../lib/push'

export function OnboardingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [support, setSupport] = useState<PushSupport>('unsupported')
  const [subscribed, setSubscribed] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    setSupport(pushSupport())
  }, [])

  const enable = async () => {
    setNote(null)
    try {
      const ok = await subscribe()
      setSubscribed(ok)
      setSupport(pushSupport())
      if (!ok) {
        setNote(
          'Notifications are off. You will still see every decision in the app — check ' +
            'My applications when you are online.',
        )
      }
    } catch {
      setNote('Could not turn on notifications. The in-app list still shows everything.')
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>Welcome aboard{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
        <span className="subtitle">
          {user?.vessel_name
            ? `You are filing for ${user.vessel_name}.`
            : 'Your agent will assign your vessel shortly.'}
        </span>
      </header>

      <section className="card stack">
        <strong>1 · How this works</strong>
        <p className="small">
          Pick a spot on the chart and tell us when you want to be there. We take it from
          there — we deal with the local harbour authority and come back with an answer. There
          is no anchoring permit office in Norway to queue at; there is us.
        </p>
        <p className="small">
          When you arrive, tap <strong>Anchor down</strong>. When you leave, tap{' '}
          <strong>Anchor up</strong>. That is the whole job.
        </p>
      </section>

      {isIos() && !isStandalone() && (
        <section className="card stack">
          <strong>2 · Add to your home screen</strong>
          <Notice tone="warn">
            On iPhone and iPad, notifications only work once this app is on your home screen.
            In a Safari tab they cannot be turned on at all.
          </Notice>
          <ol className="small" style={{ paddingLeft: '1.2em', margin: 0 }}>
            <li>
              Tap the <strong>Share</strong> button at the bottom of Safari (the square with an
              arrow).
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong>, then open Anchoring from your home screen.
            </li>
          </ol>
          <p className="caption">
            Come back to this screen after that and turn notifications on.
          </p>
        </section>
      )}

      <section className="card stack">
        <strong>{isIos() && !isStandalone() ? '3' : '2'} · Notifications</strong>
        <p className="small">
          We will notify you when a decision is made, 24 hours before your window opens, and
          when it is about to end. Nothing else.
        </p>

        {support === 'ios-needs-install' && (
          <Notice tone="warn">
            Add the app to your home screen first (step above), then this button will work.
          </Notice>
        )}
        {support === 'denied' && (
          <Notice tone="warn">
            Notifications are blocked for this app in your browser settings. You can turn them
            back on there — everything still appears in the app either way.
          </Notice>
        )}
        {support === 'unsupported' && (
          <Notice>
            This browser cannot do push notifications. Everything still appears in the app.
          </Notice>
        )}
        {(support === 'ready' || support === 'granted') && !subscribed && (
          <button type="button" className="btn btn-lg btn-block" onClick={enable}>
            Turn on notifications
          </button>
        )}
        {subscribed && <Notice tone="ok">Notifications are on for this device.</Notice>}
        {note && <p className="caption">{note}</p>}
      </section>

      <section className="card stack">
        <strong>What this app does not do</strong>
        <p className="small">
          It does not track your position. It asks for a fix only when you tap "use my
          position", "anchor down" or "anchor up", and never in the background.
        </p>
        <p className="small">
          It is not an anchor watch. Keep using whatever drag alarm you already trust — that is
          a safety-critical job and we are not going to do it worse.
        </p>
        <p className="small">
          Other captains see nothing about you unless you choose to share a specific
          application, or you both join a friend group.
        </p>
      </section>

      <button type="button" className="btn btn-lg btn-block" onClick={() => navigate('/')}>
        Open the chart
      </button>
    </div>
  )
}
